import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { StatementRow } from 'src/services/statements/models'
import { LinkSlashIcon } from '@heroicons/react/24/outline'
import { InfoHint } from 'src/components/info-hint'
import { Button } from 'src/components/button'
import { cn } from 'src/utils/style'
import { ExpenseSearchPicker } from './expense-search-picker'
import { Modal } from 'src/components/modal'
import { ExpenseCreateFormWrapper } from 'src/components/expense-create-form'
import { AiGeneratedExpense } from 'src/services/receipts'

type UnmatchedMode = 'none' | 'create' | 'link'

interface UnmatchedRowCardProps {
  row: StatementRow
  onFieldsChange?: (fields: Record<string, unknown>) => void
  editedFields?: Record<string, unknown>
  onConfirmRow?: (fields?: Record<string, unknown>) => void
  onLinkExpense: (expenseId: string) => void
  onUnlink?: () => void
  isReconciled: boolean
  isConfirming?: boolean
  isLinking: boolean
  isUnlinking?: boolean
  paymentAccountName?: string
  paymentAccountId?: string
  selected?: boolean
  onSelectChange?: (selected: boolean) => void
}

export const UnmatchedRowCard = ({
  row,
  onLinkExpense,
  onUnlink,
  isReconciled,
  isLinking,
  isUnlinking,
  paymentAccountName,
  paymentAccountId,
  selected,
  onSelectChange,
}: UnmatchedRowCardProps) => {
  const { t } = useTranslation()
  const raw = row.rawData

  const alreadyLinked = !!row.matchedExpense

  const [mode, setMode] = useState<UnmatchedMode>('none')

  // Build prefilledData for the expense form from statement row data
  const prefilledData: AiGeneratedExpense = useMemo(() => {
    const autofilled = row.autofilledFields || {}
    const amount = autofilled.amount ?? raw.amount
    return {
      name: String(autofilled.vendor ?? raw.description ?? ''),
      amount: amount ? Number(amount) : 0,
      amountCurrency: 'USD',
      expenseDate: String(autofilled.date ?? raw.date ?? ''),
      category: String(autofilled.category ?? ''),
      cashFlowType: String(autofilled.cashFlowType ?? 'debit'),
      paymentAccount: paymentAccountId || null,
      paymentAccountHint: paymentAccountName,
    }
  }, [row.autofilledFields, raw, paymentAccountId, paymentAccountName])

  const handleExpenseCreated = (expenseId: string) => {
    onLinkExpense(expenseId)
    setMode('none')
  }

  return (
    <>
      <div
        className={cn(
          'rounded-xl border border-border bg-surface px-6 py-4 shadow-sm',
          mode !== 'none' ? 'border border-primary-200' : '',
        )}
        data-testid={`unmatched-row-${row.id}`}
      >
        {/* Statement raw data summary */}
        <div className="mb-3 flex items-start justify-between">
          <div className="flex items-start gap-3">
            {!isReconciled && !alreadyLinked && onSelectChange && (
              <input
                type="checkbox"
                checked={selected ?? false}
                onChange={(e) => onSelectChange(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-border-strong text-primary-600"
                data-testid={`select-row-${row.id}`}
              />
            )}
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-text-tertiary">
                {t('statementReview.statementRow')}
              </p>
              <p className="mt-1 text-sm text-text-secondary">
                {raw.description} &middot; {raw.date} &middot; ${Number(raw.amount).toFixed(2)}
              </p>
              {raw.balance != null && (
                <p className="mt-0.5 text-xs text-text-tertiary">
                  {t('statementReview.balanceLabel', { value: Number(raw.balance).toFixed(2) })}
                </p>
              )}
            </div>
          </div>

          {!isReconciled && !alreadyLinked && (
            <div className="flex items-center gap-2">
              <Button
                variant="unstyled"
                onClick={() => setMode(mode === 'create' ? 'none' : 'create')}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  mode === 'create'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-text-secondary hover:bg-gray-200',
                )}
                data-testid={`create-new-${row.id}`}
              >
                {t('statementReview.createNew')}
              </Button>
              <Button
                variant="unstyled"
                onClick={() => setMode(mode === 'link' ? 'none' : 'link')}
                className={cn(
                  'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  mode === 'link'
                    ? 'bg-accent text-white'
                    : 'bg-gray-100 text-text-secondary hover:bg-gray-200',
                )}
                data-testid={`link-to-existing-${row.id}`}
              >
                {t('statementReview.linkToExisting')}
              </Button>
            </div>
          )}

          {alreadyLinked && (
            <div className="flex items-center gap-2">
              <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-medium text-green-700">
                {t('common.linked')}
              </span>
              {row.matchedExpenseRef ? (
                <a
                  href={`/expenses/expense/${row.matchedExpense}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-accent-700 underline decoration-accent-300 underline-offset-2 hover:decoration-accent-600"
                  data-testid={`expense-link-${row.id}`}
                >
                  {row.matchedExpenseRef.name}
                </a>
              ) : (
                <a
                  href={`/expenses/expense/${row.matchedExpense}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-accent-700 underline decoration-accent-300 underline-offset-2 hover:decoration-accent-600"
                  data-testid={`expense-link-${row.id}`}
                >
                  {t('statementReview.viewExpense')}
                </a>
              )}
              {!isReconciled && onUnlink && (
                <Button
                  variant="ghost"
                  tone="neutral"
                  size="sm"
                  shape="square"
                  iconOnly
                  tooltip={t('statementReview.unlinkRow')}
                  onClick={onUnlink}
                  disabled={isUnlinking}
                  data-testid={`unlink-${row.id}`}
                >
                  <LinkSlashIcon className="h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Link to existing expense picker */}
        {!isReconciled && !alreadyLinked && mode === 'link' && (
          <div className="mb-3">
            <ExpenseSearchPicker onSelect={onLinkExpense} isLinking={isLinking} />
          </div>
        )}

        {row.llmReasoning && (
          <p className="mt-2 flex items-center gap-1 text-xs text-text-tertiary">
            {row.confidenceScore != null && (
              <span data-testid={`confidence-${row.id}`}>
                {t('statementReview.confidence', { value: (row.confidenceScore * 100).toFixed(0) })}
              </span>
            )}
            <InfoHint
              text={t('statementReview.llmLabel', { reasoning: row.llmReasoning })}
              label={t('statementReview.whyThisMatch')}
              data-testid={`llm-reasoning-${row.id}`}
            />
          </p>
        )}
      </div>

      {/* Full expense form modal */}
      <Modal
        isOpen={mode === 'create'}
        onClose={() => setMode('none')}
        title={t('statementReview.createExpenseTitle')}
        size="xl"
      >
        <ExpenseCreateFormWrapper
          prefilledData={prefilledData}
          onSuccess={handleExpenseCreated}
          onClose={() => setMode('none')}
        />
      </Modal>
    </>
  )
}
